import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Building2, LogOut, CreditCard, Users, FileText, Link2, BarChart3, 
  PlusCircle, RefreshCw, Wallet, TrendingUp, Clock, CheckCircle, 
  AlertCircle, Download, Eye, ChevronRight, Loader2, Receipt,
  Target, UserCheck, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Credit Plans
const CREDIT_PLANS = {
  100: { INR: 500000, USD: 6000, EUR: 5500, GBP: 4750, AED: 22000, SGD: 8000 },
  200: { INR: 999999, USD: 12000, EUR: 11000, GBP: 9500, AED: 44000, SGD: 16000 },
  500: { INR: 1250000, USD: 15000, EUR: 13750, GBP: 11900, AED: 55000, SGD: 20000 },
  1000: { INR: 2000000, USD: 24000, EUR: 22000, GBP: 19000, AED: 88000, SGD: 32000 },
  2500: { INR: 3500000, USD: 42000, EUR: 38500, GBP: 33250, AED: 154000, SGD: 56000 },
  5000: { INR: 6000000, USD: 72000, EUR: 66000, GBP: 57000, AED: 264000, SGD: 96000 }
};

const formatCurrency = (amount, currency) => {
  const symbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'د.إ', SGD: 'S$' };
  return `${symbols[currency] || ''}${amount.toLocaleString()}`;
};

export const CorporateDashboard = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [showCreditPurchase, setShowCreditPurchase] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('INR');

  useEffect(() => {
    const token = localStorage.getItem('corporateToken');
    const adminData = localStorage.getItem('corporateAdmin');
    
    if (!token || !adminData) {
      navigate('/corporate/login');
      return;
    }
    
    setAdmin(JSON.parse(adminData));
    fetchDashboardData(token);
  }, [navigate]);

  const fetchDashboardData = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/enterprise/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('corporateToken');
          localStorage.removeItem('corporateAdmin');
          navigate('/corporate/login');
          return;
        }
        throw new Error('Failed to fetch dashboard data');
      }

      const data = await response.json();
      setDashboardData(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('corporateToken');
    localStorage.removeItem('corporateAdmin');
    toast.success('Logged out successfully');
    navigate('/gateway');
  };

  const handlePurchaseCredits = async (credits) => {
    const token = localStorage.getItem('corporateToken');
    
    try {
      const response = await fetch(`${API_URL}/api/enterprise/purchase-credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          credits,
          currency: selectedCurrency,
          payment_method: 'card'
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Purchase failed');
      }

      toast.success(`Payment initiated for ${credits} credits. Invoice: ${data.invoice_number}`);
      setShowCreditPurchase(false);
      
      // In production, redirect to payment gateway
      // For now, simulate successful payment
      await confirmPayment(data.payment_id);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const confirmPayment = async (paymentId) => {
    try {
      const response = await fetch(`${API_URL}/api/enterprise/payment/confirm/${paymentId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment_id: 'simulated' })
      });

      if (response.ok) {
        toast.success('Payment confirmed! Credits added to your wallet.');
        fetchDashboardData(localStorage.getItem('corporateToken'));
      }
    } catch (error) {
      console.error('Payment confirmation error:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const wallet = dashboardData?.credit_wallet || {};
  const stats = dashboardData?.statistics || {};
  const org = dashboardData?.organization || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-[#0f0f1a] to-gray-900">
      {/* Header */}
      <header className="bg-gray-900/80 backdrop-blur-lg border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">{org.company_name || 'Corporate Dashboard'}</h1>
                <p className="text-xs text-gray-400">{admin?.email}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <LanguageSelector variant="minimal" />
              <Button
                onClick={() => navigate('/corporate/buy-credits')}
                className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:opacity-90"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Buy Credits
              </Button>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-gray-700 text-gray-300 hover:bg-gray-800"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Credit Wallet Banner */}
        <Card className="bg-gradient-to-r from-blue-600/20 via-indigo-600/20 to-purple-600/20 border-blue-500/30 mb-8">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center">
                  <Wallet className="w-8 h-8 text-white" />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Credit Wallet</p>
                  <p className="text-4xl font-bold text-white">{wallet.available?.toLocaleString() || 0}</p>
                  <p className="text-gray-400 text-sm">Available Credits</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-8">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-400">{wallet.total_purchased?.toLocaleString() || 0}</p>
                  <p className="text-gray-400 text-sm">Total Purchased</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-400">{wallet.reserved?.toLocaleString() || 0}</p>
                  <p className="text-gray-400 text-sm">Reserved</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-400">{wallet.used?.toLocaleString() || 0}</p>
                  <p className="text-gray-400 text-sm">Used</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Position Profiles</p>
                  <p className="text-2xl font-bold text-white">{stats.position_assessments || 0}</p>
                </div>
                <Target className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Candidate Links</p>
                  <p className="text-2xl font-bold text-white">{stats.total_candidate_links || 0}</p>
                </div>
                <Link2 className="w-8 h-8 text-indigo-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Completed</p>
                  <p className="text-2xl font-bold text-green-400">{stats.completed_assessments || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">Pending</p>
                  <p className="text-2xl font-bold text-yellow-400">{stats.pending_assessments || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <Card 
            className="bg-gray-800/50 border-gray-700 hover:border-blue-500/50 cursor-pointer transition-all"
            onClick={() => navigate('/corporate/create-position')}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-blue-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold">Create Position Profile</h3>
                <p className="text-gray-400 text-sm">Define role requirements</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500" />
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gray-800/50 border-gray-700 hover:border-indigo-500/50 cursor-pointer transition-all"
            onClick={() => navigate('/corporate/generate-link')}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                <Link2 className="w-6 h-6 text-indigo-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold">Generate Test Link</h3>
                <p className="text-gray-400 text-sm">Create candidate assessment link</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500" />
            </CardContent>
          </Card>
          
          <Card 
            className="bg-gray-800/50 border-gray-700 hover:border-green-500/50 cursor-pointer transition-all"
            onClick={() => navigate('/corporate/compatibility')}
          >
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-green-500" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold">Compatibility Reports</h3>
                <p className="text-gray-400 text-sm">View candidate-position matches</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500" />
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Transactions */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Receipt className="w-5 h-5 text-blue-500" />
                Recent Transactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData?.recent_transactions?.length > 0 ? (
                <div className="space-y-3">
                  {dashboardData.recent_transactions.slice(0, 5).map((tx, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          tx.transaction_type === 'purchase' ? 'bg-green-500/20' : 'bg-red-500/20'
                        }`}>
                          {tx.transaction_type === 'purchase' ? (
                            <TrendingUp className="w-4 h-4 text-green-500" />
                          ) : (
                            <TrendingUp className="w-4 h-4 text-red-500 rotate-180" />
                          )}
                        </div>
                        <div>
                          <p className="text-white text-sm">{tx.description}</p>
                          <p className="text-gray-500 text-xs">{new Date(tx.created_at).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <span className={`font-semibold ${
                        tx.transaction_type === 'purchase' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {tx.transaction_type === 'purchase' ? '+' : '-'}{tx.credits}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No transactions yet</p>
              )}
            </CardContent>
          </Card>

          {/* Recent Payments */}
          <Card className="bg-gray-800/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-indigo-500" />
                Payment History
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboardData?.recent_payments?.length > 0 ? (
                <div className="space-y-3">
                  {dashboardData.recent_payments.slice(0, 5).map((payment, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          payment.status === 'completed' ? 'bg-green-500/20' : 'bg-yellow-500/20'
                        }`}>
                          {payment.status === 'completed' ? (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          ) : (
                            <Clock className="w-4 h-4 text-yellow-500" />
                          )}
                        </div>
                        <div>
                          <p className="text-white text-sm">{payment.invoice_number}</p>
                          <p className="text-gray-500 text-xs">{payment.credits_purchased} credits</p>
                        </div>
                      </div>
                      <span className="text-white font-semibold">
                        {formatCurrency(payment.amount, payment.currency)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No payments yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Credit Purchase Modal */}
      {showCreditPurchase && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <Card className="bg-gray-900 border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-blue-500" />
                Purchase Credits
              </CardTitle>
              <CardDescription className="text-gray-400">
                Select a credit plan for your organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Currency Selector */}
              <div className="mb-6 flex gap-2">
                {['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'].map(curr => (
                  <button
                    key={curr}
                    onClick={() => setSelectedCurrency(curr)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedCurrency === curr 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    {curr}
                  </button>
                ))}
              </div>

              {/* Credit Plans */}
              <div className="grid grid-cols-2 gap-4">
                {Object.entries(CREDIT_PLANS).map(([credits, prices]) => (
                  <div
                    key={credits}
                    onClick={() => handlePurchaseCredits(parseInt(credits))}
                    className="p-4 bg-gray-800/50 border border-gray-700 rounded-xl hover:border-blue-500/50 cursor-pointer transition-all"
                  >
                    <div className="text-3xl font-bold text-white mb-1">
                      {parseInt(credits).toLocaleString()}
                    </div>
                    <div className="text-gray-400 text-sm mb-3">Credits</div>
                    <div className="text-xl font-semibold text-blue-400">
                      {formatCurrency(prices[selectedCurrency], selectedCurrency)}
                    </div>
                    <div className="text-gray-500 text-xs mt-1">
                      {formatCurrency(Math.round(prices[selectedCurrency] / parseInt(credits)), selectedCurrency)}/credit
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCreditPurchase(false)}
                  className="flex-1 border-gray-700 text-gray-300"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CorporateDashboard;
